<script lang="ts">
	/**
	 * A logo, on an admin form (§4.1).
	 *
	 * The upload happens **on choosing the file**, not on submitting the form, and the field binds
	 * to the resulting uuid. That is deliberate: the alternative is holding a megabyte of base64 in
	 * the form's draft and posting it inside the product body, which turns one 400 from an
	 * unrelated field into a re-upload, and puts image bytes in the audit trail of a product write.
	 * Uploading first also means the preview is the file the server actually stored, so a rejection
	 * — wrong type, too large — is shown next to the control that caused it rather than at submit.
	 *
	 * Nothing is deleted when the image is replaced. An asset is content-addressed and may be
	 * shared by two products, so removing it here would blank a logo somewhere else on the strength
	 * of an edit to this one.
	 */

	import { Button } from 'flowbite-svelte';
	import { uploadMedia } from '../AppNetwork';
	import { toastError } from '../stores/toast';
	import { mediaURL } from '../Utilities';

	/** The stored image's uuid, or null for none. Bind to it. */
	export let uuid: string | null = null;
	/** Where the current image is served from, for the preview. Bind to it. */
	export let url: string | null = null;
	export let disabled: boolean = false;
	/** Shown in the empty state, so the admin knows what the picture is for. */
	export let label: string = 'Logo';

	let busy: boolean = false;
	let input: HTMLInputElement | null = null;

	async function fnChoose(event: Event): Promise<void> {
		const file = (event.target as HTMLInputElement).files?.[0];
		if (file === undefined) {
			return;
		}

		busy = true;
		try {
			const asset = await uploadMedia(file);
			uuid = asset.uuid;
			url = asset.url;
		} catch (error) {
			toastError('The image was not uploaded.', (error as Error).message);
		} finally {
			busy = false;
			// Cleared so choosing the *same* file again re-fires `change` — otherwise a failed
			// upload cannot be retried without picking a different file first.
			if (input !== null) {
				input.value = '';
			}
		}
	}

	function fnClear(): void {
		uuid = null;
		url = null;
	}
</script>

<div class="flex items-center gap-3">
	<div class="w-16 h-16 rounded-2xl bg-depth border-widget flex items-center justify-center overflow-hidden shrink-0">
		{#if url}
			<img src={mediaURL(url)} alt={label} class="w-full h-full object-contain" />
		{:else}
			<i class="fa-solid fa-image text-default" />
		{/if}
	</div>

	<div class="flex flex-col gap-2">
		<!-- The file input itself is hidden: it renders as an unstyleable native control that
		     ignores the design system, and its own label text cannot be set. -->
		<input
			bind:this={input}
			type="file"
			accept="image/png,image/jpeg,image/webp,image/gif"
			class="hidden"
			disabled={disabled || busy}
			on:change={fnChoose}
		/>
		<div class="flex gap-2">
			<Button size="xs" color="alternative" disabled={disabled || busy} on:click={() => input?.click()}>
				{#if busy}<i class="fa-solid fa-spinner fa-spin mr-1" />{:else}<i class="fa-solid fa-upload mr-1" />{/if}
				{url ? 'Replace' : 'Upload'}
			</Button>
			{#if url}
				<Button size="xs" color="alternative" disabled={disabled || busy} on:click={fnClear}>Remove</Button>
			{/if}
		</div>
		<p class="text-xs text-default">PNG, JPEG, WebP or GIF, up to 512 KB.</p>
	</div>
</div>
